
import jobsList from '../../../../Assets/Images/V2/assessment-service/jobs-list.svg';
import candidateComparison from '../../../../Assets/Images/V2/assessment-service/candidate-comparison.svg';
import dashboard from '../../../../Assets/Images/V2/assessment-service/dashboard.svg';
import candidatesList from '../../../../Assets/Images/V2/assessment-service/candidates-list.svg';
import apiKeys from '../../../../Assets/Images/V2/assessment-service/api-keys.svg';

export const HERO = {
	title: 'Assessment Service',
	highlight: 'Hire with Confidence. Assess Every Candidate the Same Way.',
	description: 'Assessment Service uses AI to structure interview evidence for consistent human review.'
};

export const CONSISTENCY = {
	title: 'Hiring at Scale Breaks Consistency',
	leftDescription:
		'When different interviewers evaluate different candidates, objectivity suffers. Standards shift. Great candidates can be missed, and bad hires can put teams, customers, and the business at risk.',
	rightDescription:
		'Simsola Assessment Service adds a consistent, structured layer to every evaluation so your team can make decisions based on evidence, not gut feel.'
};

export interface ValueProp {
	title: string;
	subtitle: string;
	description: string;
}

export const VALUE_PROPS: ValueProp[] = [
	{
		title: 'Speed',
		subtitle: 'Evaluate candidates in seconds',
		description:
			'Upload a transcript and get a structured, AI-assisted evaluation for your team to review. No manual grading or back-and-forth.'
	},
	{
		title: 'Consistency',
		subtitle: 'The same standard, every time',
		description:
			'Every candidate is evaluated against the same criteria regardless of who conducted the interview. Eliminate scoring drift across your team.'
	},
	{
		title: 'API-First',
		subtitle: 'Built for developers',
		description:
			'Access scores programmatically via API. Integrate Assessment Service directly into your ATS, hiring workflow, or internal tooling.'
	},
	{
		title: 'Seamless Integration',
		subtitle: 'Works with your existing stack',
		description:
			'Supports PDF, DOCX, SRT, JSON, MP4, TXT, and VTT uploads. Plug into the tools you already use, such as ChatGPT or Claude. No migration required.'
	}
];

export interface ShowcaseRow {
	id: string;
	title: string;
	body: string;
	screenshot: string;
	screenshotAlt: string;
}

export interface ShowcaseTab {
	key: 'recruiter' | 'clientAdmin';
	label: string;
	rows: ShowcaseRow[];
}

export const SHOWCASE: ShowcaseTab[] = [
	{
		key: 'recruiter',
		label: 'Recruiter',
		rows: [
			{
				id: 'create-jobs',
				title: 'Create and manage jobs',
				body:
					'Create a job for each open role and keep its candidates, transcripts and scores in one place. Every candidate is scored on the same dimensions, whichever role they applied for.',
				screenshot: jobsList,
				screenshotAlt: 'Jobs list screen'
			},
			{
				id: 'compare-candidates',
				title: 'Compare and stack rank candidates',
				body:
					'See all candidates side by side, ranked by score. Drill into individual dimensions: Communication, Critical Thinking, Execution, and Work Level.',
				screenshot: candidateComparison,
				screenshotAlt: 'Candidate comparison screen'
			}
		]
	},
	{
		key: 'clientAdmin',
		label: 'Client Admin',
		rows: [
			{
				id: 'monitor-credits',
				title: 'Monitor credits and API usage',
				body: 'Track API call volume, error rates, and credit consumption in real time. Stay on top of usage before it becomes a problem.',
				screenshot: dashboard,
				screenshotAlt: 'Dashboard screen'
			},
			{
				id: 'monitor-scores',
				title: 'Monitor candidate scores and upload transcripts',
				body: 'View every candidate across your organization, including their score, status, and uploader. Upload transcripts directly when needed.',
				screenshot: candidatesList,
				screenshotAlt: 'Candidates list screen'
			},
			{
				id: 'api-keys',
				title: 'API Key management',
				body: 'Create, revoke, and monitor API keys across your team. See usage and credit allocation per key at a glance.',
				screenshot: apiKeys,
				screenshotAlt: 'API keys screen'
			}
		]
	}
];

export interface HowItWorksStep {
	step: string;
	title: string;
	description: string;
}

export const HOW_IT_WORKS: HowItWorksStep[] = [
	{
		step: '01',
		title: 'Upload a transcript',
		description: 'Submit an interview in any supported format: PDF, DOCX, audio, or structured transcript.'
	},
	{
		step: '02',
		title: 'AI scores the candidate',
		description:
			'Every candidate is scored on the same dimensions: Communication, Critical Thinking, Execution, and Work Level. Results stay comparable across interviews.'
	},
	{
		step: '03',
		title: 'Review results your way',
		description:
			'Access scores via API response or directly in the dashboard. Compare candidates, stack-rank applicants, and move forward with clarity.'
	}
];

export interface PricingPlan {
	name: string;
	label: string;
	price: string;
	priceUnit: string;
	priceCaption: string;
	credits: string;
	description: string;
	badge: string;
	includedLabel: string;
	features: string[];
	cta: 'base' | 'demo';
	ctaLabel: string;
}

export const PRICING: PricingPlan[] = [
	{
		name: 'Base',
		label: 'Base subscription',
		price: '$150',
		priceUnit: '/ month',
		priceCaption: '',
		credits: '20 credits / month',
		description: 'For teams ready to start assessing at scale',
		badge: 'Bonus credits on signup to help your team get started.',
		includedLabel: "What's included",
		features: [
			'Dashboard UI for monitoring usage and credits',
			'Unlimited teammates (Recruiter seats)',
			'File uploads: PDF, DOCX, SRT, JSON, MP4, TXT, VTT',
			'AI score generation per candidate',
			'Job creation and management',
			'Candidate score comparison and stack ranking',
			'Review API response payloads'
		],
		cta: 'base',
		ctaLabel: 'Start a Free Trial'
	},
	{
		name: 'Enterprise',
		label: 'Enterprise',
		price: 'Custom',
		priceUnit: 'pricing',
		priceCaption: '',
		credits: 'Fully customizable contract',
		description: 'For high-volume teams with bespoke needs',
		badge: 'Custom credit allocations & SLA guarantee.',
		includedLabel: 'Everything in Base, plus',
		features: [
			'White-label ready',
			'Direct API access + ATS integration',
			'Dedicated support',
			'Custom credit allocations',
			'SLA guarantee'
		],
		cta: 'demo',
		ctaLabel: 'Book a demo'
	}
];

export const EMPLOYEE_COUNT_OPTIONS = ['1–10', '11–50', '51–200', '201–500', '501–1000', '1000+'];

export const HOW_DID_YOU_HEAR_OPTIONS = [
	'Friend or Family',
	'Google or Search Engine',
	'Social Media',
	'Online Ad',
	'Event',
	'Podcast',
	'Other'
];
